import { db, firestore } from "./FirebaseService";
import { ref, get, query, orderByKey, limitToLast, push, onValue, remove, serverTimestamp, update } from "firebase/database";
import { doc, setDoc } from "firebase/firestore";

const VENDOR_ID = process.env.VUE_APP_VENDOR_ID;
const AGENT_ID = 'main';

// chatIds minted via createChatId() that haven't had their first thread
// message written yet. The first push for a chatId in this set is bundled
// with a `created_at` sibling write so both share the same server timestamp.
const _chatIdsNeedingCreatedAt = new Set();

function userChatsPath(userId) {
  return `chats/${VENDOR_ID}/${userId}/${AGENT_ID}`;
}

function chatPath(userId, chatId) {
  return `${userChatsPath(userId)}/${chatId}`;
}

/**
 * Path to a specific chat thread under a user.
 */
export function getThreadPath(userId, chatId) {
  return `${chatPath(userId, chatId)}/thread`;
}

/**
 * Returns the latest chatId for a user, or null if none exist.
 * RTDB push keys are chronological, so the last key is the most recent chat.
 * @param {string} userId
 * @returns {Promise<string|null>}
 */
export async function getLatestChatId(userId) {
  const snapshot = await get(
    query(ref(db, userChatsPath(userId)), orderByKey(), limitToLast(1))
  );
  if (!snapshot.exists()) return null;
  return Object.keys(snapshot.val())[0];
}

/**
 * Reserves a new chatId via RTDB push key. The key isn't materialized
 * in the database until something is written under it. The first thread
 * message for this chatId will be bundled with a `created_at` sibling.
 * @param {string} userId
 * @returns {string}
 */
export function createChatId(userId) {
  const chatId = push(ref(db, userChatsPath(userId))).key;
  _chatIdsNeedingCreatedAt.add(chatId);
  return chatId;
}

/**
 * Pre-creates the Firestore chat doc for a freshly-minted chatId. The
 * portal's chat list filters on `assigned_to` (and friends), so we
 * stamp those fields explicitly on creation rather than relying on the
 * Cloud Function's `updateChatMetadata` to default them — that function
 * is now a plain merge. `assigned_to: null` is required by the security
 * rule (a lead can't claim themselves to a real portal user).
 * @param {string} userId
 * @param {string} chatId
 */
function ensureFirestoreChatDoc(userId, chatId) {
  return setDoc(
    doc(firestore, `vendors/${VENDOR_ID}/chats/${chatId}`),
    {
      lead_id: userId,
      agent_id: AGENT_ID,
      assigned_to: null,
      closed: false,
    },
    { merge: true },
  );
}

/**
 * Pushes a message into a chat's thread. If this is the first message for
 * a freshly-minted chatId, also sets `created_at` on the chat doc using a
 * multi-path update so both fields share the same server timestamp, and
 * pre-creates the Firestore chat doc in parallel.
 */
function pushToThread(userId, chatId, messageData) {
  if (_chatIdsNeedingCreatedAt.has(chatId)) {
    _chatIdsNeedingCreatedAt.delete(chatId);
    // Fire-and-forget — bot's `updateChatMetadata` later merges the
    // message metadata, so a write failure here just means the doc waits
    // for a re-send before showing up correctly in the portal.
    ensureFirestoreChatDoc(userId, chatId).catch((err) =>
      console.warn("Failed to pre-create Firestore chat doc", err),
    );
    const newKey = push(ref(db, getThreadPath(userId, chatId))).key;
    return update(ref(db), {
      [`${chatPath(userId, chatId)}/created_at`]: serverTimestamp(),
      [`${chatPath(userId, chatId)}/thread/${newKey}`]: messageData,
    });
  }
  return push(ref(db, getThreadPath(userId, chatId)), messageData);
}

/**
 * Gets the entire message thread for a user's chat.
 * @param {string} userId
 * @param {string} chatId
 * @returns chat thread represented as an object with messages as the values
 */
export async function getMessages(userId, chatId) {
  if (!chatId) return {};
  const snapshot = await get(query(ref(db, getThreadPath(userId, chatId)), orderByKey()));
  return snapshot.exists() ? snapshot.val() : {};
}

/**
 * Gets the message signature from the db
 * @returns the message signature
 */
export async function getMessageSignature() {
  const snapshot = await get(ref(db, `vendors/${VENDOR_ID}/message_signature`));
  if (snapshot.exists()) {
    return snapshot.val();
  } else {
    return 'AI Assistant';
  }
}

/**
 * Subscribes to the chat thread.
 * @param {string} userId
 * @param {string|null} chatId - if null, immediately fires an empty thread and no-ops
 * @param {Function} onThreadUpdated
 * @returns {Function} unsubscribe
 */
export function subscribeToMessages(userId, chatId, onThreadUpdated) {
  if (!chatId) {
    if (onThreadUpdated) onThreadUpdated({});
    return () => {};
  }
  return onValue(ref(db, getThreadPath(userId, chatId)), (data) => {
    if (onThreadUpdated) {
      onThreadUpdated(data.exists() ? data.val() : {});
    }
  });
}

/**
 * Subscribes to assistant typing status.
 */
export function subscribeToTyping(userId, onTypingChanged) {
  return onValue(ref(db, `assistant_typing/${userId}`), (data) => {
    const typing = data.val();
    if (onTypingChanged) {
      onTypingChanged(typing ? true : false);
    }
  });
}

/**
 * Sends a user message into the chat thread.
 */
export function sendMessage(userId, chatId, msg, action, payload) {
  const messageData = {
    role: 'user',
    content: msg,
    timestamp: serverTimestamp(),
    ...payload,
  };
  if (action) messageData.action = action;
  pushToThread(userId, chatId, messageData);
}

export function sendAction(userId, chatId, action, payload) {
  pushToThread(userId, chatId, {
    role: 'user',
    action,
    timestamp: serverTimestamp(),
    ...payload,
  });
}

export function deleteMessages(userId, chatId) {
  remove(ref(db, `assistant_typing/${userId}`));
  if (chatId) {
    remove(ref(db, chatPath(userId, chatId)));
  }
}
